import {
  CHARGE_FAILURE_CATEGORY,
  CHARGE_FAILURE_STATUS,
  formatParisDateTime,
  RECHARGE_ADMIN_URL,
  type ChargeFailureDetail,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import type { JSX } from 'react';
import { StatusBadge } from '@/components/ui/badge.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchChargeFailure } from './api.js';
import {
  chargeFailureAmount,
  chargeFailureAttempt,
  chargeFailureNextRetry,
} from './charge-failure-format.js';
import {
  Dash,
  DetailSection,
  DetailSkeleton,
  DetailSummary,
  ExternalRechargeLink,
  SlackNotification,
} from './detail-parts.js';
import { DetailSheet } from './detail-sheet.js';

function ChargeFailureDetailBody({ failure }: { failure: ChargeFailureDetail }): JSX.Element {
  const amount = chargeFailureAmount(failure);
  const attempt = chargeFailureAttempt(failure);
  const nextRetry = chargeFailureNextRetry(failure);
  const category = CHARGE_FAILURE_CATEGORY[failure.category];

  return (
    <div className="flex flex-col gap-4">
      {failure.refetchFailed ? (
        <p className="flex items-start gap-2 rounded-md border border-status-blue-border bg-status-blue-bg px-3 py-2 text-sm text-status-blue-fg">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>Données partielles : la charge n’a pas pu être relue chez Recharge</span>
        </p>
      ) : null}

      <DetailSummary title="Résumé">
        <DescriptionList>
          <DescriptionItem label="Statut">
            <StatusBadge registry={CHARGE_FAILURE_STATUS} code={failure.status} />
          </DescriptionItem>
          <DescriptionItem label="Montant">
            {amount === null ? <Dash /> : <span className="tabular-nums">{amount}</span>}
          </DescriptionItem>
          <DescriptionItem label="Tentative">
            {attempt === null ? <Dash /> : <span className="tabular-nums">{attempt}</span>}
          </DescriptionItem>
          <DescriptionItem label="Prochaine tentative">
            {nextRetry === null ? <Dash /> : <span className="tabular-nums">{nextRetry}</span>}
          </DescriptionItem>
          <DescriptionItem label="Reçu le">
            <span className="whitespace-nowrap tabular-nums">
              {formatParisDateTime(new Date(failure.receivedAt))}
            </span>
          </DescriptionItem>
        </DescriptionList>
      </DetailSummary>

      <DetailSection title="Erreur">
        <DescriptionList>
          <DescriptionItem label="Message">{failure.errorMessage ?? <Dash />}</DescriptionItem>
          <DescriptionItem label="Code">
            {failure.errorType === null ? (
              <Dash />
            ) : (
              <span className="break-all font-mono text-xs" translate="no">
                {failure.errorType}
              </span>
            )}
          </DescriptionItem>
          <DescriptionItem label="Catégorie">
            <span className="flex flex-wrap items-center gap-2">
              <StatusBadge registry={CHARGE_FAILURE_CATEGORY} code={failure.category} />
              {category.description ? (
                <span className="text-muted-foreground">{category.description}</span>
              ) : null}
            </span>
          </DescriptionItem>
        </DescriptionList>
      </DetailSection>

      <DetailSection title="Produits">
        {failure.products.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun produit connu pour cette charge</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {failure.products.map((product, index) => (
              <li key={`${product.title}-${String(index)}`}>
                <span className="tabular-nums">{product.quantity}×</span> {product.title}
              </li>
            ))}
          </ul>
        )}
      </DetailSection>

      {failure.subscriptionIds.length > 0 || failure.customerId !== null ? (
        <DetailSection title="Liens">
          <ul className="flex flex-col gap-1.5">
            {failure.subscriptionIds.map((subscriptionId) => (
              <li key={subscriptionId}>
                <ExternalRechargeLink
                  href={`${RECHARGE_ADMIN_URL}/subscriptions/${encodeURIComponent(subscriptionId)}/details`}
                >
                  {`Ouvrir l’abonnement ${subscriptionId} dans Recharge`}
                </ExternalRechargeLink>
              </li>
            ))}
            {failure.customerId === null ? null : (
              <li>
                <ExternalRechargeLink
                  href={`${RECHARGE_ADMIN_URL}/customers/${encodeURIComponent(failure.customerId)}`}
                >
                  Ouvrir le client dans Recharge
                </ExternalRechargeLink>
              </li>
            )}
          </ul>
        </DetailSection>
      ) : null}

      <DetailSection title="Notification">
        <SlackNotification slack={failure.slack} mode={failure.mode} />
      </DetailSection>
    </div>
  );
}

/**
 * Panneau de détail d'un paiement échoué (`06` §8.1), ouvert par `?id=`.
 * Le contenu vient de `GET /api/recharge/charge-failures/:id` : la liste ne
 * transporte ni les produits ni la trace Slack.
 */
export function ChargeFailureDetailSheet({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.subscriptions.chargeFailure(id ?? ''),
    queryFn: () => fetchChargeFailure(id ?? ''),
    enabled: id !== null,
  });

  const failure = query.data;
  const error = query.error instanceof ApiError ? query.error : null;
  const isNotFound = error?.status === 404;

  return (
    <DetailSheet
      open={id !== null}
      onClose={onClose}
      title={
        failure ? (
          <span className="break-all">{`Paiement échoué · ${failure.email}`}</span>
        ) : (
          'Paiement échoué'
        )
      }
    >
      {query.isPending ? (
        <DetailSkeleton label="Chargement du paiement échoué" />
      ) : query.isError ? (
        <ErrorState
          message={error?.message ?? 'Impossible de charger ce paiement échoué.'}
          detail={error?.detail}
          {...(isNotFound
            ? {}
            : {
                onRetry: () => {
                  void query.refetch();
                },
              })}
        />
      ) : (
        <ChargeFailureDetailBody failure={query.data} />
      )}
    </DetailSheet>
  );
}
